import type { FocusEvent, ReactNode, WheelEvent } from "react";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

type Props = {
  /** input 要素の `type` 値 */
  type?: HTMLInputElement["type"];
  /** フォームにエラーがあるかどうか */
  error?: boolean;
  /** コンポーネントの幅 */
  width?: number | string;
  /** オートフォーカスを行うかどうか */
  autoFocus?: boolean;
  /** コンポーネント内の先頭に表示する要素 */
  prefix?: ReactNode;
  /** コンポーネント内の末尾に表示する要素 */
  suffix?: ReactNode;
  /** コンポーネント内に表示するプレースホルダー */
  placeholder?: string;
  /** 複数行入力の際にテキストエリアの行数を指定 */
  rows?: number;
  /** prefix のクリックイベントを無視するかどうか */
  ignorePrefixClick?: boolean;
};

type ElementProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  keyof Props
>;

const InputTextArea = forwardRef<HTMLTextAreaElement, Props & ElementProps>(
  (
    {
      type,
      onFocus,
      onBlur,
      autoFocus,
      prefix,
      suffix,
      className,
      width = 180,
      disabled,
      error,
      placeholder,
      rows = 3,
      ignorePrefixClick = false,
      ...restProps
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
      ref,
      () => innerRef.current,
    );

    const handleFocus = (e: FocusEvent<HTMLTextAreaElement>) => {
      onFocus?.(e);
    };

    const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
      onBlur?.(e);
    };

    const handleWheel = (e: WheelEvent<HTMLTextAreaElement>) => {
      if (type === "number") {
        (e.target as HTMLTextAreaElement).blur();
      }
    };

    useEffect(() => {
      if (autoFocus && innerRef.current) {
        innerRef.current.focus();
      }
    }, [autoFocus]);

    const wrapperClasses = [
      "flex items-center border rounded-md",
      className,
      disabled
        ? "pointer-events-none border-neutral-border-disabled"
        : "border-neutral-muted-border",
      error ? "border-red-500" : "border-neutral-muted-border",
    ]
      .filter(Boolean)
      .join(" ");

    const inputClasses = `w-full p-3 overflow-hidden bg-transparent py-2 text-sm leading-normal ${
      disabled ? "bg-neutral-fill-disabled opacity-100" : "text-black"
    }`;

    return (
      <span
        className={wrapperClasses}
        style={{ width: typeof width === "number" ? `${width}px` : width }}
        onClick={() => innerRef.current?.focus()}
        onKeyUp={() => innerRef.current?.focus()}
        role="presentation"
      >
        {prefix && (
          <span
            className="ml-2 mr-2 shrink-0"
            onClick={ignorePrefixClick ? (e) => e.stopPropagation() : undefined}
            onKeyUp={ignorePrefixClick ? (e) => e.stopPropagation() : undefined}
            onKeyDown={
              ignorePrefixClick ? (e) => e.stopPropagation() : undefined
            }
          >
            {prefix}
          </span>
        )}
        <textarea
          {...restProps}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onWheel={handleWheel}
          disabled={disabled}
          ref={innerRef as React.Ref<HTMLTextAreaElement>}
          aria-invalid={error || undefined}
          placeholder={placeholder}
          className={inputClasses}
          rows={rows}
        />
        {suffix && <span className="ml-2 shrink-0">{suffix}</span>}
      </span>
    );
  },
);

InputTextArea.displayName = "Input";

export default InputTextArea;
